import React from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';

import { PrivateRoute } from '@trqk/components';
import { getEnableControlPanel } from '@trqk/utils';

import { FULL_PATHS, PATHS } from '../constants';
import {
    ClientEvents,
    ConfirmPasswordReset,
    ControlPanel,
    Detections,
    ForgotPassword,
    GapAnalysis,
    Insights,
    NotFound,
    Reports,
    Settings,
    SignIn,
    SignUp,
    Statements,
    StatementsPreview,
    WorkDetail,
    Tryout,
    Works,
} from '../components/pages';
import { getFeatureFlags } from '../featureFlags';
import { usePaymentPlanStatus } from '../hooks';

const redirectPath = PATHS.SIGN_IN;
const enableControlPanel = getEnableControlPanel();

export const Routes = (): JSX.Element => {
    const client = useApolloClient();
    const { clientEventsPage } = getFeatureFlags();
    const { isPublisherEdition, subscriptionSettings } = usePaymentPlanStatus();

    return (
        <Switch>
            <Route path={PATHS.SIGN_IN} exact component={SignIn} />
            <Route path={PATHS.SIGN_UP} exact component={SignUp} />
            <Route
                path={PATHS.FORGOT_PASSWORD}
                exact
                component={ForgotPassword}
            />
            <Route
                path={PATHS.CONFIRM_PASSWORD_RESET}
                exact
                component={ConfirmPasswordReset}
            />
            <Route path={PATHS.TRYOUT} exact>
                <Tryout />
            </Route>
            <Route path={PATHS.STATEMENTS_PREVIEW} exact>
                <StatementsPreview />
            </Route>
            <PrivateRoute
                appKey="web"
                client={client}
                component={Insights}
                path={PATHS.INSIGHTS}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={Statements}
                path={FULL_PATHS.STATEMENTS_CHARTS}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={Statements}
                path={FULL_PATHS.STATEMENTS_TABLE}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={Statements}
                path={FULL_PATHS.MANAGE_STATEMENTS_CHARTS}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={Statements}
                path={FULL_PATHS.MANAGE_STATEMENTS_TABLE}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={Works}
                path={`${PATHS.WORKS}/(refresh)?/:refreshTime?`}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={WorkDetail}
                path={PATHS.DETAIL}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={GapAnalysis}
                path={PATHS.GAP_ANALYSIS}
                ready={subscriptionSettings !== null}
                render={isPublisherEdition === true}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={Reports}
                path={PATHS.REPORTS}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={Detections}
                path={PATHS.DETECTIONS}
                ready={subscriptionSettings !== null}
                render={isPublisherEdition !== true}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={ClientEvents}
                path={PATHS.CLIENT_EVENTS}
                Redirect={Redirect}
                redirectPath={redirectPath}
                render={clientEventsPage}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={Settings}
                path={PATHS.SETTINGS}
                Redirect={Redirect}
                redirectPath={redirectPath}
                Route={Route}
            />
            <PrivateRoute
                appKey="web"
                client={client}
                component={ControlPanel}
                path={PATHS.CONTROL_PANEL}
                Redirect={Redirect}
                redirectPath={redirectPath}
                render={enableControlPanel}
                Route={Route}
            />
            <Route path={PATHS.LANDING} exact>
                <Redirect to={PATHS.SIGN_IN} />
            </Route>
            {/*
                This path comes last because it is not using `exact`. If no other
                paths match, we route the user to the NotFound page.
            */}
            <Route path={PATHS.LANDING} component={NotFound} />
        </Switch>
    );
};
